import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Alert,
  Button,
  Icon,
  Input,
  PageHeading,
  Pills,
  Stepper,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition, SortDirection, SortState } from 'barte-design-system';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircleIcon,
  DotsThree,
  FileXls,
  Storefront,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import {
  COMPANY_MOCK,
  INITIAL_PLANOS,
  INITIAL_SELLERS_BY_PLANO,
  formatBR,
  todayISO,
  type PlanoTaxa,
} from '../CadastroPlanoTaxas/sharedData';
import { makeEmptyGrid, type SpreadsheetValue } from '../TaxasCompanyCopiaCola/SpreadsheetGrid';
import { TaxasCartaoGrid, type TaxasCartaoState } from '../TaxasCompanyCopiaCola/TaxasCartaoGrid';
import './SheetsPreview.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type View = 'list' | 'wizard';
type WizardStep = 1 | 2 | 3;

interface GrupoCartao {
  id: string;
  nome?: string;
  taxasCartao: TaxasCartaoState;
  expandedRanges: Record<string, boolean>;
  mccs: string[];
  tipo: 'online' | 'pos' | 'tap';
  antecipacao: string;
  isDefault: boolean;
}

function tipoLabel(t: GrupoCartao['tipo']): string {
  return t === 'online' ? 'Online' : t === 'pos' ? 'POS' : 'Tap';
}

const TAXAS_BASE_ROWS = 2;
const TAXAS_BASE_COLS = 4;
const TAXAS_BASE_LABELS_COL_0 = ['PIX', 'Boleto'];
const TAXAS_BASE_LABELS_COL_2 = ['Antifraude', 'Processamento'];

const STEPS = [
  { label: 'Upload' },
  { label: 'Revisão' },
  { label: 'Processamento' },
];

// Mock: ao "importar" o sheet, preenchemos esses valores no preview do Step 2.
// Quando `withErrors=true`, injeta valores inválidos pra demonstrar a validação.
function makeTaxasBasePreFilled(withErrors: boolean = true): SpreadsheetValue {
  const grid = makeEmptyGrid(TAXAS_BASE_ROWS, TAXAS_BASE_COLS);
  for (let r = 0; r < TAXAS_BASE_ROWS; r++) {
    grid[r][0] = TAXAS_BASE_LABELS_COL_0[r] ?? '';
    grid[r][2] = TAXAS_BASE_LABELS_COL_2[r] ?? '';
  }
  grid[0][1] = 'R$ 0,70';                              // PIX
  grid[1][1] = withErrors ? 'R$ 15,00' : 'R$ 1,50';    // Boleto (15,00 = inválido)
  grid[0][3] = 'R$ 0,29';                              // Antifraude
  grid[1][3] = 'R$ 0,29';                              // Processamento
  return grid;
}

// ─── Validação (Step 2) ──────────────────────────────────────────────────────

/** Erros injetados no mock pra demonstrar a validação. Cada item indica grupo + linha + coluna.
 *  type 'over10' = MDR acima de 10%; type 'negative' = MDR negativo. */
const MOCK_ERROR_CELLS: { groupId: string; row: string; col: number; type: 'over10' | 'negative' }[] = [
  // 4 MDR acima de 10%
  { groupId: 'grp-preview-1', row: '1x',    col: 2, type: 'over10' },     // Elo
  { groupId: 'grp-preview-1', row: '13-24', col: 4, type: 'over10' },     // Hiper
  { groupId: 'grp-preview-3', row: '2-6',   col: 3, type: 'over10' },     // Amex
  { groupId: 'grp-preview-4', row: '1x',    col: 0, type: 'over10' },     // Mastercard
  // 3 MDR negativos
  { groupId: 'grp-preview-2', row: '7-12',  col: 1, type: 'negative' },   // Visa
  { groupId: 'grp-preview-4', row: '7-12',  col: 4, type: 'negative' },   // Hiper
  { groupId: 'grp-preview-6', row: '2-6',   col: 2, type: 'negative' },   // Elo
];

const ERROR_OVER10_MDRS = [12.50, 13.20, 11.30, 10.80];
const ERROR_NEGATIVE_MDRS = [-0.50, -1.20, -0.30];

/** Aplica os erros mockados sobre os grupos gerados (substitui a string da célula
 *  por uma com MDR fora da faixa válida). */
function applyMockErrors(groups: GrupoCartao[]): GrupoCartao[] {
  let over10Idx = 0;
  let negIdx = 0;
  // antecipações por grupo (devem bater com o que foi passado pro buildTaxasCartao)
  const antByGroup: Record<string, number> = {
    'grp-preview-1': 1.99, 'grp-preview-2': 1.60, 'grp-preview-3': 1.85,
    'grp-preview-4': 2.10, 'grp-preview-5': 1.75, 'grp-preview-6': 2.00,
  };
  return groups.map((g) => {
    const errorsForGroup = MOCK_ERROR_CELLS.filter((e) => e.groupId === g.id);
    if (errorsForGroup.length === 0) return g;
    const taxasCartao: TaxasCartaoState = { ...g.taxasCartao };
    for (const err of errorsForGroup) {
      const row = (taxasCartao[err.row] ?? []).slice();
      const mdr = err.type === 'over10' ? ERROR_OVER10_MDRS[over10Idx++] : ERROR_NEGATIVE_MDRS[negIdx++];
      row[err.col] = pairMdr(mdr, antByGroup[g.id] ?? 0);
      taxasCartao[err.row] = row;
    }
    return { ...g, taxasCartao };
  });
}

function parseMdrFromCell(cellStr: string): number | null {
  if (!cellStr) return null;
  const m = cellStr.match(/^(-?\d+[,.]\d+)\s*%/);
  if (!m) return null;
  return parseFloat(m[1].replace(',', '.'));
}

function parseBoletoValue(str: string): number | null {
  if (!str) return null;
  const m = str.match(/R\$\s*([\d.,]+)/);
  if (!m) return null;
  // Brazilian format: "R$ 15,00" or "R$ 1.234,56"
  return parseFloat(m[1].replace(/\./g, '').replace(',', '.'));
}

interface ValidationResult {
  hasErrors: boolean;
  messages: string[];
  errorByGroup: Record<string, Set<string>>; // groupId → "rowKey|colIdx"
  boletoInvalid: boolean;
}

function computeValidation(taxasBase: SpreadsheetValue, groups: GrupoCartao[]): ValidationResult {
  const result: ValidationResult = { hasErrors: false, messages: [], errorByGroup: {}, boletoInvalid: false };

  // Boleto está em taxasBase[1][1]
  const boletoStr = (taxasBase[1]?.[1] ?? '') as string;
  const boletoValue = parseBoletoValue(boletoStr);
  if (boletoValue !== null && boletoValue >= 10) {
    result.boletoInvalid = true;
  }

  let over10 = 0;
  let negative = 0;
  for (const g of groups) {
    const set = new Set<string>();
    for (const [rowKey, row] of Object.entries(g.taxasCartao)) {
      if (rowKey === 'debito') continue; // Débito não conta como MDR para essa validação
      if (!Array.isArray(row)) continue;
      for (let c = 0; c < row.length; c++) {
        const mdr = parseMdrFromCell(row[c]);
        if (mdr === null) continue;
        if (mdr > 10) { over10++; set.add(`${rowKey}|${c}`); }
        else if (mdr < 0) { negative++; set.add(`${rowKey}|${c}`); }
      }
    }
    if (set.size > 0) result.errorByGroup[g.id] = set;
  }

  if (result.boletoInvalid) result.messages.push('Boleto deve ser abaixo de R$ 10,00');
  if (over10 > 0) result.messages.push(`${over10} MDR ${over10 === 1 ? 'está' : 'estão'} com taxas acima de 10%`);
  if (negative > 0) result.messages.push(`${negative} MDR ${negative === 1 ? 'está' : 'estão'} negativos`);

  result.hasErrors = result.messages.length > 0;
  return result;
}

// Cada célula vira "MDR (MDR+antecipação)" (visual de duas taxas).
function pairMdr(mdr: number, antecipacao: number): string {
  const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;
  return `${fmt(mdr)} (${fmt(mdr + antecipacao)})`;
}

function buildTaxasCartao(mdrBase: number, ant: number, debitoBase: number): TaxasCartaoState {
  const fmtSingle = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;
  return {
    '1x':    [pairMdr(mdrBase + 0.00, ant), pairMdr(mdrBase + 0.32, ant), pairMdr(mdrBase + 1.99, ant), pairMdr(mdrBase - 0.22, ant), pairMdr(mdrBase + 1.27, ant)],
    '2-6':   [pairMdr(mdrBase - 0.71, ant), pairMdr(mdrBase + 1.18, ant), pairMdr(mdrBase + 2.46, ant), pairMdr(mdrBase + 0.06, ant), pairMdr(mdrBase + 2.52, ant)],
    '7-12':  [pairMdr(mdrBase - 0.59, ant), pairMdr(mdrBase + 1.91, ant), pairMdr(mdrBase + 2.68, ant), pairMdr(mdrBase + 0.06, ant), pairMdr(mdrBase + 2.92, ant)],
    '13-24': [pairMdr(mdrBase + 2.40, ant), pairMdr(mdrBase + 2.40, ant), pairMdr(mdrBase + 2.40, ant), pairMdr(mdrBase + 2.40, ant), pairMdr(mdrBase + 2.40, ant)],
    debito:  [fmtSingle(debitoBase), fmtSingle(debitoBase + 0.10), fmtSingle(debitoBase + 0.20), fmtSingle(debitoBase - 0.20), fmtSingle(debitoBase + 0.05)],
  };
}

function makePreviewGroups(withErrors: boolean = true): GrupoCartao[] {
  const base: GrupoCartao[] = [
    // ─── 3 grupos Online ────────────────────────────────────────────────
    {
      id: 'grp-preview-1',
      taxasCartao: buildTaxasCartao(1.05, 1.99, 0.95),
      expandedRanges: {},
      mccs: ['8999'],
      tipo: 'online',
      antecipacao: '130% da CDI',
      isDefault: true,
    },
    {
      id: 'grp-preview-2',
      taxasCartao: buildTaxasCartao(0.95, 1.60, 0.88),
      expandedRanges: {},
      mccs: ['5411', '5912'],
      tipo: 'online',
      antecipacao: '125% da CDI',
      isDefault: false,
    },
    {
      id: 'grp-preview-3',
      taxasCartao: buildTaxasCartao(1.15, 1.85, 1.02),
      expandedRanges: {},
      mccs: ['8011', '8021'],
      tipo: 'online',
      antecipacao: '120% da CDI',
      isDefault: false,
    },
    // ─── 3 grupos POS ───────────────────────────────────────────────────
    {
      id: 'grp-preview-4',
      taxasCartao: buildTaxasCartao(1.20, 2.10, 1.10),
      expandedRanges: {},
      mccs: ['8999'],
      tipo: 'pos',
      antecipacao: '135% da CDI',
      isDefault: false,
    },
    {
      id: 'grp-preview-5',
      taxasCartao: buildTaxasCartao(1.10, 1.75, 1.00),
      expandedRanges: {},
      mccs: ['5411', '5912'],
      tipo: 'pos',
      antecipacao: '128% da CDI',
      isDefault: false,
    },
    {
      id: 'grp-preview-6',
      taxasCartao: buildTaxasCartao(1.30, 2.00, 1.18),
      expandedRanges: {},
      mccs: ['8011', '8021'],
      tipo: 'pos',
      antecipacao: '122% da CDI',
      isDefault: false,
    },
  ];
  return withErrors ? applyMockErrors(base) : base;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SheetsPreview() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');
  const [planos, setPlanos] = useState<PlanoTaxa[]>(INITIAL_PLANOS);
  const [novoPlanoId, setNovoPlanoId] = useState<number | null>(null);

  // Lista
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [sortState, setSortState] = useState<SortState>({ key: 'dataCriacao', direction: 'desc' });
  const [sellersByPlano] = useState<Record<number, string[]>>(INITIAL_SELLERS_BY_PLANO);
  const [rowMenuOpenId, setRowMenuOpenId] = useState<number | null>(null);
  const rowMenuRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Fecha o menu de opções ao clicar fora da linha
  useEffect(() => {
    if (rowMenuOpenId === null) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      const wrap = rowMenuRefs.current[rowMenuOpenId!];
      if (wrap && !wrap.contains(target)) setRowMenuOpenId(null);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [rowMenuOpenId]);

  // Wizard
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 — arquivo OU link
  const [sheetFile, setSheetFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [step1Error, setStep1Error] = useState<string | null>(null);

  // Step 2 — dados read-only "carregados" do sheet
  const [previewTaxasBase, setPreviewTaxasBase] = useState<SpreadsheetValue>(makeTaxasBasePreFilled);
  const [previewGroups, setPreviewGroups] = useState<GrupoCartao[]>(makePreviewGroups);
  const [previewNome, setPreviewNome] = useState('Plano Importado');

  // Step 3 — processamento
  const [processingPhase, setProcessingPhase] = useState<0 | 1 | 2>(0);
  const [showSuccess, setShowSuccess] = useState(false);

  // Loading entre step 1 → step 2 ("Importando dados da planilha")
  const [importLoading, setImportLoading] = useState(false);

  // Quantas vezes o usuário já entrou no Step 2. Na 1ª vez mostramos erros
  // de validação (Boleto > R$10, MDR > 10%, MDR negativos); a partir da 2ª
  // simulamos que os erros foram corrigidos na planilha e os dados ficam limpos.
  const [step2Visits, setStep2Visits] = useState(0);

  // ─── List actions ─────────────────────────────────────────────────────────

  function openWizard() {
    setView('wizard');
    setStep(1);
    setSheetFile(null);
    setStep1Error(null);
    setPreviewTaxasBase(makeTaxasBasePreFilled(true));
    setPreviewGroups(makePreviewGroups(true));
    setPreviewNome('Plano Importado');
    setProcessingPhase(0);
    setShowSuccess(false);
    setNovoPlanoId(null);
    setStep2Visits(0);
  }

  function returnToList() {
    setView('list');
  }

  // ─── Step 1: upload / link ────────────────────────────────────────────────

  function validateAndSetFile(file: File | null) {
    setStep1Error(null);
    if (!file) { setSheetFile(null); return; }
    const ok = /\.(xlsx|xls|csv)$/i.test(file.name) ||
               file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
               file.type === 'application/vnd.ms-excel' ||
               file.type === 'text/csv';
    if (!ok) {
      setStep1Error('Formato inválido. Aceitamos XLSX, XLS ou CSV.');
      setSheetFile(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setStep1Error('O arquivo excede o limite de 10 MB.');
      setSheetFile(null);
      return;
    }
    setSheetFile(file);
  }

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    validateAndSetFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    validateAndSetFile(file);
  }

  const step1Ready = sheetFile !== null;

  // ─── Wizard navigation ────────────────────────────────────────────────────

  function handleNext() {
    if (step === 1) {
      if (!step1Ready) {
        setStep1Error('Faça upload de um arquivo ou cole um link válido.');
        return;
      }
      setStep1Error(null);
      setImportLoading(true);
      setTimeout(() => {
        setImportLoading(false);
        const nextVisit = step2Visits + 1;
        setStep2Visits(nextVisit);
        // Da 2ª visita em diante, simula que o usuário corrigiu a planilha:
        // re-importa os mesmos dados, agora sem erros (Boleto OK, sem MDR fora da faixa).
        if (nextVisit >= 2) {
          setPreviewTaxasBase(makeTaxasBasePreFilled(false));
          setPreviewGroups(makePreviewGroups(false));
        }
        setStep(2);
      }, 2000);
    } else if (step === 2) {
      setStep(3);
    }
  }

  function handlePrev() {
    if (step === 2) setStep(1);
    else if (step === 1) returnToList();
  }

  // ─── Processamento ────────────────────────────────────────────────────────

  useEffect(() => {
    if (view !== 'wizard' || step !== 3) return;
    setProcessingPhase(0);
    setShowSuccess(false);
    const t1 = setTimeout(() => setProcessingPhase(1), 3000);
    const t2 = setTimeout(() => {
      setProcessingPhase(2);
      const novoId = Math.max(...planos.map((p) => p.id)) + 1;
      const novo: PlanoTaxa = {
        id: novoId,
        nome: previewNome.trim() || 'Plano importado por Sheet',
        tipoAntecipado: false,
        tipoFluxoTempo: false,
        qtdSellers: 0,
        dataCriacao: todayISO(),
      };
      setPlanos((prev) => [...prev, novo]);
      setNovoPlanoId(novoId);
    }, 6000);
    const t3 = setTimeout(() => setShowSuccess(true), 7500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [view, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalTaxas = useMemo(() => {
    const base = 24 * 5 * 6 + 3 * 6; // 738
    return previewGroups.reduce((sum, g) => sum + base * Math.max(g.mccs.length, 1), 0);
  }, [previewGroups]);

  // ─── List rendering ───────────────────────────────────────────────────────

  const columns: ColumnDefinition<PlanoTaxa>[] = [
    { key: 'id', title: 'ID', dataIndex: 'id', width: 70, render: (v) => <Text variant="body-sm">{v as number}</Text> },
    {
      key: 'nome',
      title: 'Planos de taxas',
      dataIndex: 'nome',
      sortable: true,
      render: (v) => <Text variant="body-sm">{v as string}</Text>,
    },
    {
      key: 'dataCriacao',
      title: 'Data de criação',
      dataIndex: 'dataCriacao',
      width: 140,
      sortable: true,
      render: (v) => <Text variant="body-sm">{formatBR(v as string)}</Text>,
    },
    {
      key: 'planoAntecipado',
      title: 'Plano Antecipado',
      dataIndex: 'tipoAntecipado',
      width: 170,
      render: (_v, r) => (
        <div className="sp-cell-center">
          {r.isDefaultAntecipado ? (
            <Pills label="Plano default" state="success" variant="fill" size="sm" />
          ) : /antecipado/i.test(r.nome) ? (
            <Icon icon={Check} size={18} weight="bold" className="sp-cell-check" />
          ) : (
            <Text variant="body-sm">—</Text>
          )}
        </div>
      ),
    },
    {
      key: 'planoFluxoTempo',
      title: 'Plano Fluxo do tempo',
      dataIndex: 'tipoFluxoTempo',
      width: 180,
      render: (_v, r) => (
        <div className="sp-cell-center">
          {r.isDefaultFluxoTempo ? (
            <Pills label="Plano default" state="success" variant="fill" size="sm" />
          ) : /fluxo do tempo/i.test(r.nome) ? (
            <Icon icon={Check} size={18} weight="bold" className="sp-cell-check" />
          ) : (
            <Text variant="body-sm">—</Text>
          )}
        </div>
      ),
    },
    {
      key: 'qtdSellers',
      title: 'Lojistas',
      dataIndex: 'qtdSellers',
      width: 130,
      render: (_v, r) => {
        const count = sellersByPlano[r.id]?.length ?? 0;
        return (
          <Pills
            label={`${count} lojista${count !== 1 ? 's' : ''}`}
            state="default"
            variant="light"
            size="sm"
            icon={Storefront}
          />
        );
      },
    },
    {
      key: 'options',
      title: '',
      dataIndex: 'id',
      width: 48,
      render: (_v, r) => (
        <div
          className="sp-row-options-wrap"
          ref={(el) => { rowMenuRefs.current[r.id] = el; }}
        >
          <button
            type="button"
            className="sp-row-options-btn"
            onClick={(e) => {
              e.stopPropagation();
              setRowMenuOpenId((prev) => (prev === r.id ? null : r.id));
            }}
            aria-label={`Ações do plano ${r.nome}`}
            aria-haspopup="menu"
            aria-expanded={rowMenuOpenId === r.id}
          >
            <Icon icon={DotsThree} size={20} weight="bold" />
          </button>
          {rowMenuOpenId === r.id && (
            <div className="sp-row-options-menu" role="menu">
              <button
                type="button"
                className="sp-row-options-item"
                onClick={(e) => { e.stopPropagation(); setRowMenuOpenId(null); }}
                role="menuitem"
              >
                Tornar default
              </button>
              <button
                type="button"
                className="sp-row-options-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setRowMenuOpenId(null);
                  navigate(`/backoffice/sheets/preview/plano/${r.id}/edicao-massa`);
                }}
                role="menuitem"
              >
                Editar
              </button>
              <button
                type="button"
                className="sp-row-options-item"
                onClick={(e) => { e.stopPropagation(); setRowMenuOpenId(null); }}
                role="menuitem"
              >
                Renomear
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  const sortedPlanos = useMemo(() => {
    if (!sortState.direction) return planos;
    const arr = [...planos];
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortState.key === 'nome') {
        av = a.nome.toLowerCase();
        bv = b.nome.toLowerCase();
      } else if (sortState.key === 'dataCriacao') {
        av = a.dataCriacao;
        bv = b.dataCriacao;
      }
      if (av < bv) return sortState.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [planos, sortState]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedPlanos.slice(start, start + pageSize);
  }, [page, sortedPlanos]);
  const totalPages = Math.ceil(sortedPlanos.length / pageSize);

  function handleSort(key: string, direction: SortDirection) {
    setSortState({ key, direction });
    setPage(1);
  }

  function renderListView() {
    return (
      <>
        <PageHeading
          title={COMPANY_MOCK.nome}
          primaryActionLabel="Novo plano"
          onPrimaryAction={openWizard}
        />

        <div className="sp-company-pills-row">
          <Pills label={`ID: ${COMPANY_MOCK.id}`} state="default" variant="light" size="sm" />
          <Pills label={`CNPJ: ${COMPANY_MOCK.cnpj}`} state="default" variant="light" size="sm" />
        </div>

        <div className="content-table sp-content-table">
          <Table<PlanoTaxa>
            data={pagedData}
            columns={columns}
            rowKey={(r) => r.id}
            emptyText="Nenhum plano cadastrado."
            sortState={sortState}
            onSort={handleSort}
            onRowClick={(r) => navigate(`/backoffice/sheets/preview/plano/${r.id}`)}
            pagination={{
              currentPage: page,
              totalPages,
              totalItems: sortedPlanos.length,
              onPageChange: setPage,
            }}
          />
        </div>
      </>
    );
  }

  // ─── Step 1: upload ou link ───────────────────────────────────────────────

  function renderStep1() {
    return (
      <div className="sp-step-body">
        <div className="sp-field">
          <Text variant="label-md" className="sp-field-label">Importar dados da planilha</Text>

          {!sheetFile && (
            <label
              htmlFor="sp-file-input"
              className={`sp-dropzone${isDragging ? ' is-dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
            >
              <div className="sp-dropzone-icon">
                <Icon icon={UploadSimple} size={32} weight="regular" />
              </div>
              <Text variant="body-base">
                <span className="sp-dropzone-link">Clique para fazer upload</span> ou arraste e solte
              </Text>
              <Text variant="caption-md">Arquivos suportados: XLSX, XLS, CSV (até 10 MB)</Text>
              <input
                id="sp-file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                className="sp-dropzone-input"
                onChange={onFilePick}
              />
            </label>
          )}

          {sheetFile && (
            <div className="sp-file-card">
              <Icon icon={FileXls} size={28} weight="regular" />
              <div className="sp-file-card-meta">
                <Text variant="label-base" className="sp-file-card-name">{sheetFile.name}</Text>
                <Text variant="caption-md" className="sp-file-card-size">
                  {(sheetFile.size / 1024).toFixed(1)} KB
                </Text>
              </div>
              <button
                className="sp-file-card-remove"
                onClick={() => setSheetFile(null)}
                aria-label="Remover arquivo"
                type="button"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          )}
        </div>

        {step1Error && (
          <Alert
            variant="error"
            title="Não foi possível avançar"
            description={step1Error}
            showCloseButton={false}
          />
        )}
      </div>
    );
  }

  // ─── Step 2: review read-only ────────────────────────────────────────────

  const validation = useMemo(
    () => computeValidation(previewTaxasBase, previewGroups),
    [previewTaxasBase, previewGroups],
  );

  function renderStep2() {
    const pixValue = (previewTaxasBase[0]?.[1] ?? 'R$ 0,70') as string;
    const boletoValue = (previewTaxasBase[1]?.[1] ?? 'R$ 1,50') as string;
    const antifraudeValue = (previewTaxasBase[0]?.[3] ?? 'R$ 0,29') as string;
    const processamentoValue = (previewTaxasBase[1]?.[3] ?? 'R$ 0,29') as string;

    return (
      <div className="sp-step-body">
        <Input
          label="Nome do plano"
          placeholder="Ex.: Plano antecipado Go Pagos"
          value={previewNome}
          onChange={(e) => setPreviewNome((e.target as HTMLInputElement).value)}
        />

        {/* ── Validação: banner amarelo com lista de erros ─────────────── */}
        {validation.hasErrors && (
          <div className="sp-validation-banner" role="alert">
            <Text variant="label-base" className="sp-validation-banner-title">
              Alguns valores precisam ser revisados na planilha:
            </Text>
            <ul className="sp-validation-list">
              {validation.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Taxas (read-only) — horizontal ──────────────────────────── */}
        <div className="sp-taxas-base-grid">
          <div className="sp-tb-label">PIX</div>
          <div className="sp-tb-label">Boleto</div>
          <div className="sp-tb-label">Antifraude</div>
          <div className="sp-tb-label">Processamento</div>
          <div className="sp-tb-value">{pixValue}</div>
          <div className={`sp-tb-value${validation.boletoInvalid ? ' is-error' : ''}`}>{boletoValue}</div>
          <div className="sp-tb-value">{antifraudeValue}</div>
          <div className="sp-tb-value">{processamentoValue}</div>
        </div>

        <div className="sp-divider" />

        {/* ── Grupos (read-only) ────────────────────────────────────── */}
        <div className="sp-field">
          {previewGroups.map((g, idx) => (
            <div key={g.id} className="sp-group-block">
              {/* Header consolidado: label Grupo X + pills + Antecipação à direita */}
              <div className="sp-group-pill-row">
                <Pills
                  label={`Grupo ${idx + 1}`}
                  state="default"
                  variant="fill"
                  size="sm"
                />
                {g.isDefault && (
                  <Pills label="Default" state="success" variant="fill" size="sm" />
                )}
                <Pills label={tipoLabel(g.tipo)} state="warning" variant="light" size="sm" />
                {g.mccs.map((m) => (
                  <Pills key={m} label={m} state="accent" variant="light" size="sm" />
                ))}
                <div className="sp-group-pill-spacer" />
                <span className="sp-antecipacao-pill">
                  Antecipação: <strong>{g.antecipacao}</strong>
                </span>
              </div>

              {/* Taxas de Cartão */}
              <div className="sp-readonly-wrap">
                <TaxasCartaoGrid
                  readOnly
                  value={g.taxasCartao}
                  onChange={() => {}}
                  expanded={g.expandedRanges}
                  onToggleExpand={(rangeKey) =>
                    setPreviewGroups((prev) =>
                      prev.map((x) =>
                        x.id === g.id
                          ? { ...x, expandedRanges: { ...x.expandedRanges, [rangeKey]: !x.expandedRanges[rangeKey] } }
                          : x,
                      ),
                    )
                  }
                  extraRows={[{ key: 'debito', label: 'Débito' }]}
                  errorCells={validation.errorByGroup[g.id]}
                  bandeiraPadrao={idx % 2 === 0 ? 'Mastercard' : 'Visa'}
                />
              </div>

              {idx < previewGroups.length - 1 && <div className="sp-group-divider" />}
            </div>
          ))}
        </div>

        <Alert
          variant="info"
          title="As variações de taxas serão geradas automaticamente:"
          description="A partir da base de cálculo e para cada grupo: Parcela (1 a 24), bandeira (Mastercard, Visa, Elo, Amex, Hiper), dias de repasse (D+1, D+2, D+3, D+10, D+14, D+30) e método de pagamento."
          showCloseButton={false}
        />
      </div>
    );
  }

  // ─── Step 3: processamento + sucesso ─────────────────────────────────────

  function renderStep3() {
    const phases = [
      'Calculando variações de taxa…',
      'Processando cadastro…',
      'Tudo pronto!',
    ];
    if (showSuccess) {
      const CONFETTI_COLORS = ['#EF3B7D', '#21A23E', '#1D70B8', '#F5A623', '#7B61FF'];
      const confettiCount = 14;
      return (
        <div className="sp-state-screen">
          <div className="sp-success-wrap">
            <Icon icon={CheckCircleIcon} size={56} weight="fill" className="sp-success-icon" />
            <div className="sp-confetti" aria-hidden>
              {Array.from({ length: confettiCount }).map((_, i) => {
                const angle = (360 / confettiCount) * i;
                const distance = 56 + (i % 3) * 12;
                const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
                const delay = (i % 5) * 40;
                return (
                  <span
                    key={i}
                    className="sp-confetti-piece"
                    style={{
                      ['--angle' as any]: `${angle}deg`,
                      ['--distance' as any]: `${distance}px`,
                      ['--delay' as any]: `${delay}ms`,
                      background: color,
                    }}
                  />
                );
              })}
            </div>
          </div>
          <Text variant="body-xl" className="sp-state-screen-title">
            Plano "{previewNome}" criado com sucesso
          </Text>
          <Pills
            label={`${totalTaxas.toLocaleString('pt-BR')} taxas cadastradas em ${previewGroups.length} grupo${previewGroups.length !== 1 ? 's' : ''} de cartão`}
            state="success"
            variant="light"
            size="base"
          />
        </div>
      );
    }
    return (
      <div className="sp-state-screen">
        <div className="sp-spinner" aria-label="Processando" />
        <Text variant="body-xl" className="sp-state-screen-title" role="status" aria-live="polite">
          {phases[processingPhase]}
        </Text>
      </div>
    );
  }

  function renderWizardFooter() {
    if (step === 3) {
      if (showSuccess) {
        return (
          <>
            <div />
            <div className="sp-footer-right">
              <Button variant="outline" onClick={returnToList}>
                Voltar para planos
              </Button>
              <Button
                onClick={() => {
                  if (novoPlanoId) navigate(`/backoffice/sheets/preview/plano/${novoPlanoId}`);
                  else returnToList();
                }}
              >
                Ver plano
              </Button>
            </div>
          </>
        );
      }
      return null;
    }
    return (
      <>
        <Button
          variant="ghost"
          onClick={handlePrev}
          iconLeft={<Icon icon={ArrowLeft} size={18} weight="regular" />}
        >
          {step === 1 ? 'Cancelar' : 'Voltar'}
        </Button>
        <Button
          onClick={handleNext}
          iconRight={<Icon icon={ArrowRight} size={18} weight="regular" />}
          disabled={(step === 1 && !step1Ready) || (step === 2 && validation.hasErrors)}
        >
          {step === 2 ? 'Confirmar e processar' : 'Próximo'}
        </Button>
      </>
    );
  }

  function renderWizardView() {
    return (
      <>
        <PageHeading title="Novo plano de taxas" />

        <div className="sp-company-pill-below-title">
          <Pills
            label={`Empresa: ${COMPANY_MOCK.nome}`}
            state="default"
            variant="light"
            size="sm"
          />
        </div>

        <div className="sp-stepper">
          <Stepper steps={STEPS} currentStep={step} />
        </div>

        {importLoading ? (
          <div className="sp-state-screen">
            <div className="sp-spinner" aria-label="Importando" />
            <Text variant="body-xl" className="sp-state-screen-title">
              Importando dados da planilha…
            </Text>
          </div>
        ) : (
          <>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </>
        )}

        {!importLoading && (
          <div className="sp-footer">
            {renderWizardFooter()}
          </div>
        )}
      </>
    );
  }

  return (
    <BackofficeLayout activeItem="sheets-preview">
      <main className="content">
        <div className="content-inner sp-page">
          {view === 'list' ? renderListView() : renderWizardView()}
        </div>
      </main>
    </BackofficeLayout>
  );
}
